import React, { useMemo, useCallback } from 'react';
import StaticPage, { StaticPageData } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/static-page';
import { LOOM_CONTENT_LOOK } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content-look';
import { TasticWithCMS } from 'types/tastic';
import useSanityPermissionsChecker from 'helpers/hooks/useSanityPermissionsChecker';
import { ContentImage } from 'components/commercetools-ui/types';

function getMediaFileUrl(media: unknown): string | undefined {
  if (!media) return undefined;
  if (typeof media === 'string') return media;

  const asset = media as Record<string, unknown>;
  if (typeof asset.url === 'string' && asset.url) return asset.url;

  const nestedAsset = asset.asset as Record<string, unknown> | undefined;
  if (nestedAsset && typeof nestedAsset.url === 'string' && nestedAsset.url) return nestedAsset.url;

  const fields = asset.fields as Record<string, unknown> | undefined;
  const file = fields?.file as Record<string, unknown> | undefined;
  if (file && typeof file.url === 'string' && file.url) return file.url;

  return undefined;
}

function mimeTypeFromUrl(url: string): string {
  if (/\.webm(\?|#|$)/i.test(url)) return 'video/webm';
  if (/\.mp4(\?|#|$)/i.test(url)) return 'video/mp4';
  return 'video/mp4';
}

function getMediaMimeType(media: unknown, url?: string): string | undefined {
  if (media && typeof media === 'object') {
    const asset = media as Record<string, unknown>;
    const nestedAsset = asset.asset as Record<string, unknown> | undefined;
    if (typeof nestedAsset?.mimeType === 'string' && nestedAsset.mimeType) {
      return nestedAsset.mimeType;
    }

    const fields = asset.fields as Record<string, unknown> | undefined;
    const file = fields?.file as Record<string, unknown> | undefined;
    if (typeof file?.mimeType === 'string' && file.mimeType) {
      return file.mimeType;
    }
  }

  return url ? mimeTypeFromUrl(url) : undefined;
}

interface Props extends TasticWithCMS<unknown, unknown, any> {}

const StaticPageTastic: React.FC<Props> = ({ data, customerGroup }) => {
  const { checkNestedItemPermission, checkItemPermission } = useSanityPermissionsChecker();

  // Helper: Check permissions only at the current level (without checking nested children)
  const checkTopLevelPermission = useCallback(
    (item: any, customerGroup: any) => {
      if (!item) return false;
      // Create a copy without nested elements to check only parent level permissions
      const { navigation, sections, ...itemWithoutNested } = item;
      return checkNestedItemPermission(itemWithoutNested, customerGroup);
    },
    [checkNestedItemPermission],
  );

  // Get static page data from Sanity structure
  const cmsStaticPageDataSource = (data as any)?.cmsStaticPage?.dataSource;
  const staticPageFields = cmsStaticPageDataSource?.fields;

  // Check top-level permissions for the static page (without checking nested sections/navigation)
  const hasPagePermission = useMemo(() => {
    if (!staticPageFields) {
      return false;
    }
    const result = checkTopLevelPermission(staticPageFields, customerGroup);
    return result;
  }, [staticPageFields, customerGroup, checkTopLevelPermission]);

  // Map and filter navigation items by permissions
  const navigation = useMemo(() => {
    if (!hasPagePermission || !staticPageFields?.navigation) return [];

    return staticPageFields.navigation
      .filter((item: any) => checkNestedItemPermission(item, customerGroup))
      .map((item: any) => ({
        id: item.id || '',
        label: item.label || '',
        href: item.href || '',
      }));
  }, [staticPageFields?.navigation, customerGroup, checkNestedItemPermission, hasPagePermission]);

  const mapRegularSection = useCallback(
    (section: any) => {
      const filteredMedia: ContentImage[] =
        section.images
          ?.filter((item: any) => checkNestedItemPermission(item, customerGroup))
          .map((item: any) => {
            if (item._type === 'videoAsset') {
              const videoUrl = getMediaFileUrl(item.file);
              const videoMimeType = getMediaMimeType(item.file, videoUrl);
              return {
                src: '',
                alt: item.title || item.description || '',
                link: item.link || '',
                imageWidth: item.imageWidth || '',
                videoUrl,
                videoMimeType,
              };
            }
            const imageUrl = item.file?.fields?.file?.url || '';
            return {
              src: imageUrl,
              alt: item.title || item.description || '',
              link: item.link || '',
              imageWidth: item.imageWidth || '',
              width: item.file?.fields?.file?.details?.image?.width,
              height: item.file?.fields?.file?.details?.image?.height,
            };
          })
          .filter((item: ContentImage) => item.videoUrl || item.src) || [];

      const hasPdfPermission = section.pdfFile ? checkNestedItemPermission(section.pdfFile, customerGroup) : true;

      return {
        id: section.id || section._key || '',
        title: section.title,
        subtitle: section.subtitle,
        subtitlePosition: section.subtitlePosition,
        content: section.content,
        displaySectionAsAccordion: section.displaySectionAsAccordion,
        accordionTitle: section.accordionTitle,
        tableHeadColor: section.tableHeadColor,
        tableHeadTextColor: section.tableHeadTextColor,
        tableBodyColor: section.tableBodyColor,
        tableBodyTextColor: section.tableBodyTextColor,
        textContentWidth: section.textContentWidth,
        images: filteredMedia.length > 0 ? filteredMedia : undefined,
        imageLayout: section.imageLayout,
        imagePosition: section.imagePosition,
        imageWidth: section.imageWidth,
        pdfFile: hasPdfPermission ? section.pdfFile?.fields?.file?.url : undefined,
      };
    },
    [checkNestedItemPermission, customerGroup],
  );

  const mapExpandableGroup = useCallback(
    (section: any) => {
      if (!section.items) {
        return [];
      }

      return section.items
        .filter((item: any) => {
          const permitted = checkNestedItemPermission(item, customerGroup);
          return permitted;
        })
        .map((item: any) => ({
          id: item._key || '',
          content: item.body,
          displaySectionAsAccordion: true,
          accordionTitle: item.label,
        }));
    },
    [checkNestedItemPermission, customerGroup],
  );

  // Map and filter sections by high-level permissions, then filter elements within each section
  const sections = useMemo(() => {
    if (!hasPagePermission || !staticPageFields?.sections) {
      return [];
    }

    const result = staticPageFields.sections
      .filter((section: any) => {
        if (section._type === 'componentExpandableGroup') {
          return checkItemPermission(section, customerGroup);
        }
        return checkNestedItemPermission(section, customerGroup);
      })
      .flatMap((section: any) => {
        if (section._type === 'componentExpandableGroup') {
          return mapExpandableGroup(section);
        }
        return mapRegularSection(section);
      });

    return result;
  }, [staticPageFields?.sections, customerGroup, checkNestedItemPermission, checkItemPermission, hasPagePermission, mapRegularSection, mapExpandableGroup]);

  // Build the mapped data
  const mappedData: StaticPageData = useMemo(() => {
    if (!hasPagePermission) {
      return {
        navigationTitle: '',
        navigation: [],
        sections: [],
      };
    }

    return {
      pageTitle: staticPageFields?.pageTitle,
      navigationTitle: staticPageFields?.name || staticPageFields?.navigationTitle || '',
      navigation,
      sections,
    };
  }, [hasPagePermission, staticPageFields, navigation, sections]);

  // Don't render if user doesn't have permission
  if (!hasPagePermission) {
    return null;
  }

  return (
    <StaticPage initialData={mappedData} accordionButtonClassName={LOOM_CONTENT_LOOK.faqAccordionButtonClassName} />
  );
};

export default StaticPageTastic;
